import invariant from 'invariant';
import { ParsedQuery } from 'query-string';

import { ListingType } from 'algolia-api/types/ListingType';
import { SearchFacet } from 'algolia-api/types/SearchFacet';
import { SearchFilters } from 'algolia-api/types/SearchFilters';
import { SearchFiltersWithType } from 'algolia-api/types/SearchFiltersWithType';
import { StaffTypeFilter } from 'algolia-api/types/StaffTypeFilter';
import {
  filterNulls,
  mapObject,
  objectEmpty,
  remove,
  setEquals,
  updateObject,
} from 'utils/functional';

type AllSearchTypes = StaffTypeFilter | ListingType | 'CMS_PAGE';

export function searchableFacetsForType(type: AllSearchTypes): Array<string> {
  // @ts-expect-error TS(2740): Type '{ ALL: never[]; JOB: string[]; INTERNSHIP: s... Remove this comment to see the full error message
  const byType: Record<AllSearchTypes, Array<string>> = {
    ALL: [],
    JOB: ['orgAreasOfFocusFacet', 'jobKeywordsFacet'],
    INTERNSHIP: ['orgAreasOfFocusFacet', 'internshipKeywordsFacet'],
    VOLOP: ['volopAreasOfFocusFacet', 'volopKeywordsFacet'],
    ORG: ['orgAreasOfFocusFacet'],
  };
  return byType[type] || [];
}

export function sideFacetsForType(
  searchFacets: Readonly<Record<string, SearchFacet>>,
  type: AllSearchTypes,
): Array<SearchFacet> {
  // @ts-expect-error TS(2739): Type '{ ALL: never[]; JOB: string[]; INTERNSHIP: s... Remove this comment to see the full error message
  const facetTypeToKeys: Record<AllSearchTypes, Array<string>> = {
    ALL: ['locationTypeFacet'],
    JOB: [
      'locationTypeFacet',
      'jobKeywordsFacet',
      'orgAreasOfFocusFacet',
      'educationFacet',
      'professionalLevelFacet',
      'employmentTypeFacet',
      'orgTypeFacet',
      'statusFacet',
      'localeFacet',
      'recencyFacet',
      'jobFamilyFacet', // not shown in sidebar
    ],
    INTERNSHIP: [
      'locationTypeFacet',
      'internshipKeywordsFacet',
      'orgAreasOfFocusFacet',
      'orgTypeFacet',
      'paymentFacet',
      'statusFacet',
      'localeFacet',
      'recencyFacet',
    ],
    ORG: [
      'locationTypeFacet',
      'orgAreasOfFocusFacet',
      'orgTypeFacet',
      'statusFacet',
      'localeFacet',
    ],
    // Anything added for actions must also be added in VolopSearchFiltersSidebarContainer.
    VOLOP: [
      'actionTypeFacet',
      'locationTypeFacet',
      'volopAreasOfFocusFacet',
      'volopKeywordsFacet',
      'hasLocationFacet',
      'actionDateFacet',
      'volopSourceFacet', // Not shown in sidebar.
      'localeFacet',
      'recencyFacet',
    ],
    GRAD_PROGRAM: [
      'locationTypeFacet',
      'publicPrivateFacet',
      'statusFacet',
      'gradAreasOfStudyFacet',
    ],
  };
  const facetKeys = facetTypeToKeys[type] || [];
  const includedFilters = facetKeys
    .filter((key) => searchFacets[key])
    .map((key) => searchFacets[key]);
  return includedFilters;
}

// stateSearchFilters takes the redux filter state and
// outputs an "almost" saved-search-serializable data structure
export function stateSearchFilters(
  searchFacets: Record<string, SearchFacet>,
  facetFilters: SearchFilters | SearchFiltersWithType<AllSearchTypes>,
) {
  let filters = {} as SearchFiltersWithType<AllSearchTypes>;

  if (facetFilters && facetFilters.type !== 'ALL') {
    // TODO: Fix this the next time the file is edited.
    // @ts-expect-error fix types
    filters.type = facetFilters.type;
  }

  Object.values(searchFacets).forEach((facet) => {
    const setting = facetFilters[facet.id];

    if (facet.type === 'boolean') {
      // TODO: Fix this the next time the file is edited.
      // @ts-expect-error fix types
      if (setting === 'YES') filters[facet.id] = true;
      // TODO: Fix this the next time the file is edited.
      // @ts-expect-error fix types
      else if (setting === 'NO') filters[facet.id] = false; // take no action for 'ALL'
    } else if (facet.type === 'array') {
      // Check if the array has all options in it aswell
      if (!setting || setting.length === 0) return;
      const allOptionsSelected = facet.options.every(
        (i) => Array.isArray(setting) && setting.includes(i),
      );
      // TODO: Fix this the next time the file is edited.
      // @ts-expect-error fix types
      if (!allOptionsSelected) filters[facet.id] = setting;
    } else if (facet.type === 'string') {
      if (!setting || setting.length === 0) return;
      const [optionSelected] = setting;
      // TODO: Fix this the next time the file is edited.
      // @ts-expect-error fix types
      filters[facet.id] = optionSelected;
    } else if (facet.type === 'date') {
      // @ts-expect-error fix types
      const s = setting as Record<string, number>;

      if (!objectEmpty(s)) {
        // TODO: Fix this the next time the file is edited.
        // @ts-expect-error fix types
        filters = filterNulls({
          ...filters,
          [facet.id]: undefined,
          ...s,
        });
      }
    } else {
      throw new Error(`invalid facet type: ${facet.type}`);
    }
  });
  return filters;
}
// serializeFiltersForSavedSearch takes the redux filter state and outputs
// a saved-search-serializable data structure. See `stateSearchFilters`
export function serializeFiltersForSavedSearch({
  searchFacets,
  filters,
  queryParams,
}: {
  filters: SearchFilters | SearchFiltersWithType<AllSearchTypes>;
  searchFacets: Record<string, SearchFacet>;
  queryParams: ParsedQuery;
}) {
  return mapObject(
    stateSearchFilters(searchFacets, filters),
    (key: string, value) => {
      if (['type', 'startsLT', 'endsGT'].includes(key)) {
        return [key, value];
      }

      if (searchFacets[key].type === 'string') {
        return [
          searchFacets[key].name,
          queryParams[searchFacets[key].name] || value,
        ];
      }

      return [searchFacets[key].name, value];
    },
  );
}

export function areFiltersDefault(
  facets: Array<SearchFacet>,
  facetsThatArentLoaded: Array<string>,
  filters: SearchFilters,
) {
  const facetsToCheck = remove(facets, (f) =>
    facetsThatArentLoaded.includes(f.id),
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const arrayCheck = (facet: any, filter: any) =>
    !filter ||
    (Array.isArray(filter) && setEquals(facet.options, filter)) ||
    filter === 'ALL';

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dateCheck = (facet: any, filter: any) =>
    !filter ||
    objectEmpty(filter) ||
    (typeof filter === 'object' && Object.values(filter).every((v) => !v));

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const defaultCheck = (facet: any, filter: any) => !filter || filter === 'ALL';

  return facetsToCheck.every((f) => {
    const filter = filters[f.id];
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line no-nested-ternary
    return f.type === 'array'
      ? arrayCheck(f, filter)
      : f.type === 'date'
      ? dateCheck(f, filter)
      : defaultCheck(f, filter);
  });
}

export function initialFacetFilters(
  facets: Array<SearchFacet>,
  facetsThatArentLoaded: Array<string>,
  queryParams: Readonly<Record<string, string>>,
) {
  return updateObject(
    // @ts-expect-error TS(2556): A spread argument must either have a tuple type or... Remove this comment to see the full error message
    ...(facets || [])
      .filter(
        (f) =>
          !facetsThatArentLoaded.includes(f.id) ||
          (queryParams[f.name] && !f.lazy),
      )
      .map((facet) => {
        if (facet.type === 'array') {
          const paramValue = queryParams[facet.name];

          return paramValue
            ? {
                [facet.id]: Array.isArray(paramValue)
                  ? paramValue
                  : [paramValue],
              }
            : undefined;
        }

        if (facet.type === 'boolean') {
          // special handling for true/false instead of 'YES'/'NO' used for landing page cms
          // @ts-expect-error TS(2367): This condition will always return 'false' since th... Remove this comment to see the full error message
          if (queryParams[facet.name] === true) {
            return {
              [facet.id]: 'YES',
            };
          }

          // @ts-expect-error TS(2367): This condition will always return 'false' since th... Remove this comment to see the full error message
          if (queryParams[facet.name] === false) {
            return {
              [facet.id]: 'NO',
            };
          }
        }

        if (facet.type === 'date') {
          // @ts-expect-error TS(2532): Object is possibly 'undefined'.
          const dateParams = facet.names.reduce((acc, cur) => {
            if (queryParams[cur]) {
              // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
              // eslint-disable-next-line no-param-reassign
              acc[cur] = parseInt(queryParams[cur], 10);
            }
            return acc;
          }, {});
          return {
            [facet.id]: dateParams,
          };
        }

        return queryParams[facet.name]
          ? {
              [facet.id]: queryParams[facet.name],
            }
          : undefined;
      }),
  );
}

function getFilterValue(
  facet: SearchFacet,
  paramValue: string | string[],
): string[] {
  invariant(facet.type === 'array', 'Facet type must be `array`');

  return Array.isArray(paramValue) ? paramValue : [paramValue];
}

type PostSearchFacetKeys =
  | 'articleAuthorFacet'
  | 'articleTagsFacet'
  | 'articleCategoriesFacet';

type PostSearchFilters = {
  articleAuthorFacet: string[];
  articleTagsFacet: string[];
  articleCategoriesFacet: string[];
};

export function setFiltersForPostSearch(
  facets: Record<PostSearchFacetKeys, SearchFacet>,
  queryParams: Readonly<Record<string, string>>,
) {
  const initialFacetKeys = Object.keys(facets) as PostSearchFacetKeys[];

  const initialFilters = {} as PostSearchFilters;

  initialFacetKeys
    .filter((key) => queryParams[facets[key].name])
    .forEach((key) => {
      const paramValue = queryParams[facets[key].name];

      initialFilters[key] = getFilterValue(facets[key], paramValue);
    });

  return initialFilters;
}

export function initialFacetFiltersForMainSearch(
  searchFacets: Record<string, SearchFacet>,
  facetsThatArentLoaded: Array<string>,
  queryParams: Readonly<{
    actionType?: string;
    actionTypeFacet?: string;
    synonymId?: string;
    type: StaffTypeFilter | ListingType;
    saved?: string;
  }>,
  isRemoteQuery: boolean | null | undefined = false,
  reset = false,
) {
  const { type } = queryParams;
  invariant(type, `missing type: ${JSON.stringify(queryParams)}`);

  const actionType = queryParams.actionTypeFacet || queryParams.actionType;
  const f = initialFacetFilters(
    sideFacetsForType(searchFacets, type),
    facetsThatArentLoaded,
    queryParams,
  );
  let hasRefreshed = false;

  // @NOTE - This is kind of a hack and using a deprecated API
  // Maybe we can reconsider how we set the "ALL" filter?
  if (
    typeof window !== 'undefined' &&
    window?.performance.navigation.type === 1
  ) {
    hasRefreshed = true;
  }

  // special handling for user entering "Remote" as location
  if (isRemoteQuery) {
    f.locationTypeFacet = ['REMOTE'];
  }

  if (
    (f.locationTypeFacet && ['ALL', 'ORG', 'GRAD_PROGRAM'].includes(type)) ||
    !f.locationTypeFacet
  ) {
    f.locationTypeFacet = ['HYBRID', 'ONSITE', 'REMOTE'];
  }

  if (
    !reset &&
    !hasRefreshed &&
    !queryParams.saved &&
    type === 'VOLOP' &&
    !actionType
  ) {
    f.actionTypeFacet = ['VOLOP'];
  } else if (reset && type === 'VOLOP') {
    f.actionTypeFacet = ['VOLOP', 'EVENT', 'LOAN', 'DONATION', 'PETITION'];
  } else if (actionType && !f.actionTypeFacet) {
    f.actionTypeFacet = Array.isArray(actionType) ? actionType : [actionType];
  }

  return {
    type,
    ...f,
  };
}

// convert filter ids to filter names
// also convert YES/NO/ALL to bool (or null)
export function convertFilterIdsToNames(
  searchFacets: Record<string, SearchFacet>,
  {
    filters,
  }: {
    filters: SearchFilters;
  },
) {
  return filterNulls<SearchFilters>(
    mapObject(filters, (key, value) => {
      if (key === 'type') {
        return ['type', value];
      }

      const facet = searchFacets[key];

      // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
      if (facet && facet.name) {
        if (facet.type === 'boolean') {
          if (value === 'YES') {
            return [facet.name, true];
          }

          if (value === 'NO') {
            return [facet.name, false];
          }

          return [facet.name, null];
        }

        if (facet.type === 'array') {
          const allOptionsSelected = facet.options.every(
            (i) => Array.isArray(value) && value.includes(i),
          );
          if (allOptionsSelected) {
            return [facet.name, null];
          }
        }

        return [facet.name, value];
      }

      return [key, value];
    }),
  );
}
